import React, { useState } from 'react';
import { ui, msgColor } from '../../styles/shared';
import { Device } from '../../types';
import { getDeviceIcon } from '../../styles/shared';
import { reInterviewDevice } from '../../services/zigbeeApi';

// Stilar från den gemensamma stilfilen
const card = ui.card;
const btn = ui.btn;
const inputStyle = ui.input;
const labelStyle = ui.label;
const hintStyle = ui.hint;

interface Props {
    device: Device;
    sensorData: any;
    settingsForm: Record<string, string>;
    settingsSaving: boolean;
    settingsMsg: string | null;
    onLoadSensorData: () => void;
    onSettingsChange: (fn: (prev: Record<string, string>) => Record<string, string>) => void;
    onSaveSettings: (payload: Record<string, any>) => void;
    readOnly?: boolean;
}

const SensorControl: React.FC<Props> = ({
    device, sensorData,
    settingsForm, settingsSaving, settingsMsg,
    onLoadSensorData, onSettingsChange, onSaveSettings, readOnly,
}) => {
    const [reInterviewState, setReInterviewState] = useState<'idle' | 'loading' | 'ok' | 'error'>('idle');
    const [reInterviewMsg, setReInterviewMsg] = useState<string | null>(null);

    const isZigbee = device.ieee_address?.startsWith('0x');

    const handleReInterview = async () => {
        setReInterviewState('loading');
        setReInterviewMsg(null);
        try {
            const result = await reInterviewDevice(device.id);
            setReInterviewState(result.success ? 'ok' : 'error');
            setReInterviewMsg(result.message);
        } catch (err: any) {
            setReInterviewState('error');
            setReInterviewMsg(err?.response?.data?.message || err.message || 'Okänt fel');
        }
    };

    return (
    <div style={card}>
        <h3 style={{ marginTop: 0 }}>{getDeviceIcon(device.type)} Avläsning</h3>

        {!readOnly && (
            <div style={{ marginBottom: '20px' }}>
                <button onClick={onLoadSensorData} style={{ ...btn('#007bff'), padding: '12px 24px' }}>Läs av givaren</button>
            </div>
        )}

        {sensorData && (
            <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '4px', border: '1px solid #ddd' }}>
                <h4 style={{ marginTop: 0 }}>Aktuella värden:</h4>
                {sensorData.temperature !== undefined && <p style={{ fontSize: '20px', fontWeight: 'bold', margin: '8px 0' }}>🌡️ Temperatur: {sensorData.temperature} °C</p>}
                {sensorData.humidity !== undefined && <p style={{ fontSize: '20px', fontWeight: 'bold', margin: '8px 0' }}>💧 Fukt: {sensorData.humidity} %</p>}
                {sensorData.pressure !== undefined && <p style={{ fontSize: '20px', fontWeight: 'bold', margin: '8px 0' }}>🌬️ Lufttryck: {sensorData.pressure} hPa</p>}
                {sensorData.illuminance_lux !== undefined && <p style={{ fontSize: '20px', fontWeight: 'bold', margin: '8px 0' }}>☀️ Ljusstyrka: {sensorData.illuminance_lux} lux</p>}
                {sensorData.occupancy !== undefined && <p style={{ fontSize: '20px', fontWeight: 'bold', margin: '8px 0' }}>🚶 Rörelse: {sensorData.occupancy ? 'Ja' : 'Nej'}</p>}
                {sensorData.contact !== undefined && <p style={{ fontSize: '20px', fontWeight: 'bold', margin: '8px 0' }}>🚪 Dörr/fönster: {sensorData.contact ? 'Stängd' : 'Öppen'}</p>}
                {sensorData.state !== undefined && <p style={{ fontSize: '20px', fontWeight: 'bold', margin: '8px 0' }}>💡 Läge: {sensorData.state === 'ON' ? 'PÅ' : sensorData.state === 'OFF' ? 'AV' : sensorData.state}</p>}
                {sensorData.battery !== undefined && <p style={{ fontSize: '16px', margin: '8px 0', color: '#555' }}>🔋 Batteri: {sensorData.battery} %</p>}
                {sensorData.linkquality !== undefined && <p style={{ fontSize: '14px', margin: '8px 0', color: '#888' }}>📶 Signalstyrka: {sensorData.linkquality}</p>}
                {Object.keys(sensorData)
                    .filter(k => !['temperature','humidity','pressure','illuminance_lux','occupancy','contact','state','battery','linkquality'].includes(k))
                    .map(k => <p key={k} style={{ fontSize: '16px', margin: '8px 0' }}>{k}: {String(sensorData[k])}</p>)}
            </div>
        )}

        {device.temperature !== undefined && (
            <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#e7f3ff', borderRadius: '4px' }}>
                <strong>Senast kända:</strong> {device.temperature} °C
            </div>
        )}
        {device.humidity !== undefined && (
            <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#e7f3ff', borderRadius: '4px' }}>
                <strong>Senast kända:</strong> {device.humidity} %
            </div>
        )}
        {device.motion !== undefined && (
            <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#e7f3ff', borderRadius: '4px' }}>
                <strong>Senast kända:</strong> Rörelse {device.motion ? 'ja' : 'nej'}
            </div>
        )}
        {device.contact !== undefined && (
            <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#e7f3ff', borderRadius: '4px' }}>
                <strong>Senast kända:</strong> {device.contact ? 'Stängd' : 'Öppen'}
            </div>
        )}

        {device.type === 'motion' && !readOnly && (
            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
                <h4 style={{ marginTop: 0 }}>Inställningar för rörelsevakt</h4>
                <div style={{ maxWidth: '380px' }}>
                    <label style={labelStyle}>Tid innan rörelse nollställs (sekunder)</label>
                    <input type="number" min="0" max="86400" step="1" placeholder="90"
                        value={settingsForm.occupancy_timeout_seconds ?? ''}
                        onChange={e => onSettingsChange(p => ({ ...p, occupancy_timeout_seconds: e.target.value }))}
                        style={inputStyle} />
                    <span style={hintStyle}>Så lång tid efter senaste rörelsen innan larmet släcks av sig självt. 0 stänger av det.</span>
                </div>
                <button disabled={settingsSaving}
                    onClick={() => {
                        const payload: Record<string, any> = {};
                        if (settingsForm.occupancy_timeout_seconds !== undefined && settingsForm.occupancy_timeout_seconds !== '') {
                            payload.occupancy_timeout_seconds = Number(settingsForm.occupancy_timeout_seconds);
                        }
                        onSaveSettings(payload);
                    }}
                    style={{ ...btn(settingsSaving ? '#6c757d' : '#28a745'), marginTop: '12px', cursor: settingsSaving ? 'not-allowed' : 'pointer' }}>
                    {settingsSaving ? 'Sparar…' : 'Spara'}
                </button>
                {settingsMsg && <p style={{ margin: '10px 0 0 0', fontSize: '14px', color: msgColor(settingsMsg) }}>{settingsMsg}</p>}
            </div>
        )}

        {(device.type === 'temperature' || device.type === 'humidity') && !readOnly && (
            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
                <h4 style={{ marginTop: 0 }}>Så ofta enheten rapporterar</h4>
                <p style={{ fontSize: '13px', color: '#666', margin: '0 0 14px 0' }}>
                    Styr hur ofta enheten rapporterar. Tillämpas automatiskt nästa gång enheten vaknar.
                    Lämna tomt för standard (minst 300 s, högst 3600 s, ändring 0,5 °C / 3 %).
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', maxWidth: '480px' }}>
                    <div>
                        <label style={labelStyle}>Temperatur, minst (s)</label>
                        <input type="number" min="0" placeholder="300"
                            value={settingsForm.tempMin ?? ''}
                            onChange={e => onSettingsChange(p => ({ ...p, tempMin: e.target.value }))}
                            style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>Temperatur, högst (s)</label>
                        <input type="number" min="0" placeholder="3600"
                            value={settingsForm.tempMax ?? ''}
                            onChange={e => onSettingsChange(p => ({ ...p, tempMax: e.target.value }))}
                            style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>Temperaturändring (0,01 °C)</label>
                        <input type="number" min="0" placeholder="50"
                            value={settingsForm.tempChange ?? ''}
                            onChange={e => onSettingsChange(p => ({ ...p, tempChange: e.target.value }))}
                            style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>Fukt, minst (s)</label>
                        <input type="number" min="0" placeholder="300"
                            value={settingsForm.humMin ?? ''}
                            onChange={e => onSettingsChange(p => ({ ...p, humMin: e.target.value }))}
                            style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>Fukt, högst (s)</label>
                        <input type="number" min="0" placeholder="3600"
                            value={settingsForm.humMax ?? ''}
                            onChange={e => onSettingsChange(p => ({ ...p, humMax: e.target.value }))}
                            style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>Fuktändring (0,01 %)</label>
                        <input type="number" min="0" placeholder="300"
                            value={settingsForm.humChange ?? ''}
                            onChange={e => onSettingsChange(p => ({ ...p, humChange: e.target.value }))}
                            style={inputStyle} />
                    </div>
                </div>
                <button disabled={settingsSaving}
                    onClick={() => {
                        const payload: Record<string, any> = {};
                        (['tempMin', 'tempMax', 'tempChange', 'humMin', 'humMax', 'humChange'] as const).forEach(k => {
                            if (settingsForm[k] !== undefined && settingsForm[k] !== '') {
                                payload[k] = Number(settingsForm[k]);
                            } else {
                                payload[k] = null;
                            }
                        });
                        onSaveSettings(payload);
                    }}
                    style={{ ...btn(settingsSaving ? '#6c757d' : '#28a745'), marginTop: '12px', cursor: settingsSaving ? 'not-allowed' : 'pointer' }}>
                    {settingsSaving ? 'Sparar…' : 'Spara rapportinställningar'}
                </button>
                {settingsMsg && <p style={{ margin: '10px 0 0 0', fontSize: '14px', color: msgColor(settingsMsg) }}>{settingsMsg}</p>}
            </div>
        )}

        {!readOnly && (
            <>
        {isZigbee && (
            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
                <h4 style={{ marginTop: 0 }}>Fråga enheten på nytt</h4>
                {device.battery != null ? (
                    <p style={{ fontSize: '14px', color: '#888', margin: '0 0 12px 0' }}>
                        Det här fungerar inte för batteridrivna enheter — de sover och kan inte svara.
                        Inställningarna skickas i stället automatiskt nästa gång enheten ansluter.
                    </p>
                ) : (
                    <p style={{ fontSize: '14px', color: '#555', margin: '0 0 12px 0' }}>
                        Samordnaren frågar enheten på nytt om vad den kan och ställer om rapporteringen.
                        Använd om givaren slutat rapportera efter en ny parkoppling.
                    </p>
                )}
                <button
                    disabled={reInterviewState === 'loading' || device.battery != null}
                    onClick={handleReInterview}
                    title={device.battery != null ? 'Går inte för batteridrivna enheter — de sover' : undefined}
                    style={{ ...btn(reInterviewState === 'loading' || device.battery != null ? '#6c757d' : '#6f42c1'), cursor: reInterviewState === 'loading' || device.battery != null ? 'not-allowed' : 'pointer', opacity: device.battery != null ? 0.5 : 1 }}>
                    {reInterviewState === 'loading' ? 'Frågar enheten…' : '🔄 Fråga enheten på nytt'}
                </button>
                {reInterviewMsg && (
                    <p style={{ margin: '10px 0 0 0', fontSize: '14px', color: reInterviewState === 'ok' ? '#28a745' : '#dc3545' }}>
                        {reInterviewMsg}
                    </p>
                )}
            </div>
        )}
            </>
        )}
    </div>
    );
};

export default SensorControl;
